import { useRef, useState } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import type { PopoverRootActions } from '@base-ui/react/popover'
import { Button, Popover } from '@systemeio/components'
import { ActivityIcon, CalendarIcon, Mail01Icon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { MAX_VISIBLE_BIO_CHARS } from 'shared/constants'
import { useActivityRelativeDate } from 'shared/hooks/use-community-relative-date'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { getTimeDifference } from 'shared/utils/date-time/get-time-difference'
import { getContactUrl } from 'shared/utils/url-utils'
import { UserInfoPopoverSkeleton } from 'modules/community/components/user-info-popover/user-info-popover-skeleton'
import useUserInfo from 'modules/community/hooks/use-user-info'
import { getTruncatedText } from './utils/get-truncated-text'

interface UserInfoPopoverProps {
  userId: number
  className?: string
}

const UserInfoPopoverContent = ({ userId }: Pick<UserInfoPopoverProps, 'userId'>): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { data: userInfo } = useUserInfo({ userId })
  const joinedRelative = useActivityRelativeDate(userInfo?.createdAt)
  const lastActiveRelative = useActivityRelativeDate(userInfo?.lastActiveAt)
  const [isExpanded, setIsExpanded] = useState(false)

  const isActive = userInfo?.lastActiveAt
    ? // TODO: i think we can come up with better useTimer, and use it here to get timed updates
      // eslint-disable-next-line @eslint-react/purity
      getTimeDifference(Date.now(), userInfo.lastActiveAt, 'minute') <= 5
    : false

  return userInfo ? (
    <div className="flex w-full flex-col items-start gap-3 px-3 py-5 sm:min-h-[250px] sm:flex-row">
      <img
        src={userInfo.profileImageUrl}
        alt={userInfo.displayName}
        className="size-40 shrink-0 rounded-full"
        width={160}
        height={160}
      />
      <div className="flex w-full min-w-0 grow flex-col gap-3">
        <div className="max-w-[304px] overflow-hidden wrap-break-word text-ellipsis">
          <Popover.Title className="line-clamp-2 max-w-full text-3xl">
            {userInfo.displayName}
          </Popover.Title>
        </div>
        {isActive ? (
          <div className="inline-flex items-center gap-2">
            <div className="size-3 rounded-full bg-bg-success" />
            <span>{t('community.user_info.active_now')}</span>
          </div>
        ) : (
          !!userInfo.lastActiveAt && (
            <div className="inline-flex items-center gap-2">
              <ActivityIcon className="size-5" />
              <span className="text-base font-normal text-text-secondary">
                {t('community.user_info.last_activity_at', {
                  date: lastActiveRelative,
                })}
              </span>
            </div>
          )
        )}
        {joinedRelative && isAuthorizedUser(user) && (
          <div className="inline-flex items-center gap-2">
            <CalendarIcon className="size-5" />
            <span className="text-base font-normal text-text-secondary">
              {t('members.list.member.joined')} {joinedRelative}
            </span>
          </div>
        )}
        {!!userInfo.email && (
          <div className="inline-flex items-center gap-2">
            <Mail01Icon className="size-5" />
            <a
              href={`mailto:${userInfo.email}`}
              className="min-w-0 text-base font-normal wrap-break-word text-text-secondary"
              title={userInfo.email}
            >
              {userInfo.email}
            </a>
          </div>
        )}
        {!!userInfo.bio && (
          <div className="flex flex-col items-start">
            <span className="max-w-[304px] text-base/6 font-normal wrap-break-word whitespace-normal text-text-secondary">
              {getTruncatedText(userInfo.bio, isExpanded, MAX_VISIBLE_BIO_CHARS)}
              {userInfo.bio.length > MAX_VISIBLE_BIO_CHARS && (
                <button
                  data-testid="user-info-bio-toggle"
                  className="text-base font-normal text-text-link"
                  onClick={() => {
                    setIsExpanded(!isExpanded)
                  }}
                >
                  ...
                  {isExpanded
                    ? t('community.user_info.hide.button')
                    : t('community.user_info.readmore.button')}
                </button>
              )}
            </span>
          </div>
        )}
        {userInfo.contactId && (
          <Button
            variant="ghost-gray"
            className="w-full"
            render={
              <LinkWithoutPrefetch
                href={getContactUrl(userInfo.contactId)}
                target="_blank"
                rel="noreferrer"
              />
            }
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
            }}
          >
            {t('community.user_info.open_contact_page')}
          </Button>
        )}
      </div>
    </div>
  ) : (
    <UserInfoPopoverSkeleton />
  )
}

export const UserInfoPopover = ({
  children,
  userId,
  className,
}: PropsWithChildren<UserInfoPopoverProps>): ReactNode => {
  const popupRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<PopoverRootActions>(null)

  return (
    <Popover.Root
      actionsRef={actionsRef}
      onOpenChange={(open, details) => {
        if (
          !open &&
          details.reason === 'trigger-hover' &&
          popupRef.current?.contains(document.activeElement) === true
        ) {
          details.cancel()
        }
      }}
    >
      <div
        onClick={event => {
          event.stopPropagation()
        }}
        className={cn('truncate', className)}
      >
        <Popover.Trigger
          openOnHover
          delay={600}
          closeDelay={100}
          className="flex h-fit w-full items-center justify-around gap-2.5 focus:outline-hidden"
        >
          {children}
        </Popover.Trigger>
      </div>
      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          sideOffset={10}
          collisionAvoidance={{ side: 'shift', align: 'shift' }}
        >
          <Popover.Popup
            data-testid="user-info-popover-popup"
            ref={popupRef}
            onBlur={event => {
              if (!event.currentTarget.contains(event.relatedTarget)) actionsRef.current?.close()
            }}
            className="flex max-h-(--available-height) w-[calc(100vw-2rem)] max-w-[500px] flex-col overflow-y-auto rounded-lg bg-bg-primary text-sm shadow-2"
          >
            <UserInfoPopoverContent userId={userId} />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
